import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import { dailyWeatherConditionsTestIDs, dailyWeatherPageTestIDs, myWeatherPageTestIDs, Theme } from '@constants';
import { useSegmentTrackWeatherForecast } from '@hooks';
import { navigationService } from '@services';
import { dailyForecastState } from '@store';

import { isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { WeatherTileDetail } from '../../myWeather/WeatherTileDetail/WeatherTileDetail';
import { TWCText } from '../../primitives/TWCText/TWCText';

const getDaily = data => {
  if (!data?.daypart?.length) {
    return;
  }

  /**
   * Check whether to display "Today" or "Tonight" data when "Today" tile is selected based on API response
   * If every element in daypart[0] is null, then assume it's past 3PM and to display "Tonight"
   */
  const isPast3PM = Object.keys(data?.daypart[0])
    .map(key => data?.daypart[0][key][0])
    .every(element => !element);

  const { iconCodeExtend, temperature, wxPhraseLong } = data.daypart[0];
  const daysOfWeek = data.dayOfWeek;
  const days = 3;

  return Array.from({ length: days }, (_, index) => {
    const modifiedIndex = isPast3PM ? index + 1 : index;

    return {
      iconCodeExtend: iconCodeExtend[modifiedIndex + modifiedIndex],
      weather: isDisplayable(wxPhraseLong[modifiedIndex + modifiedIndex])
        ? wxPhraseLong[modifiedIndex + modifiedIndex]
        : missingValue,
      nightTemperature: isDisplayable(temperature[modifiedIndex + modifiedIndex + 1])
        ? `${temperature[modifiedIndex + modifiedIndex + 1]}°`
        : missingValue,
      rawNightTemperature: isDisplayable(temperature[modifiedIndex + modifiedIndex + 1])
        ? `${temperature[modifiedIndex + modifiedIndex + 1]}`
        : missingValue,
      temperature: isDisplayable(temperature[modifiedIndex + modifiedIndex])
        ? `${temperature[modifiedIndex + modifiedIndex]}°`
        : missingValue,
      rawTemperature: isDisplayable(temperature[modifiedIndex + modifiedIndex])
        ? `${temperature[modifiedIndex + modifiedIndex]}`
        : missingValue,
      title: isDisplayable(daysOfWeek[modifiedIndex]?.slice(0, 3))
        ? daysOfWeek[modifiedIndex].slice(0, 3)
        : missingValue,
      day: isDisplayable(daysOfWeek[modifiedIndex]) ? daysOfWeek[modifiedIndex] : missingValue,
    };
  });
};

const DailyWeatherConditionsTileAPI = ({ nextFocusDown, nextFocusLeft, nextFocusRight, nextFocusUp }) => {
  const { data, error, isLoading } = useRecoilValue(dailyForecastState);

  return (
    <DailyWeatherConditionsTileUI
      data={data}
      error={error}
      isLoading={isLoading}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
    />
  );
};

const tPath = 'dailyWeatherConditionsTile';

const DailyWeatherConditionsTileUI = ({
  data,
  error,
  isLoading,
  nextFocusDown,
  nextFocusLeft,
  nextFocusRight,
  nextFocusUp,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const dailyData = data ? getDaily(data) : undefined;

  if (!dailyData) {
    error = 'Daily data failed to parse.';
  }

  const [forecastType, setSegmentTrackWeatherForecast] = useSegmentTrackWeatherForecast();

  const handleOnPress = () => {
    setSegmentTrackWeatherForecast(forecastType.DAILY);
    navigationService.navigate(dailyWeatherPageTestIDs.dailyWeatherPage);
  };

  const day1Label = error
    ? ''
    : t('tileDayAccessibilityLabel', {
        day: dailyData?.[0].day,
        weather: dailyData?.[0].weather,
        temperature: dailyData?.[0].rawTemperature,
        nightTemperature: dailyData?.[0].rawNightTemperature,
      }).replace(missingValue, 'unknown');

  const day2Label = error
    ? ''
    : t('tileDayAccessibilityLabel', {
        day: dailyData?.[1].day,
        weather: dailyData?.[1].weather,
        temperature: dailyData?.[1].rawTemperature,
        nightTemperature: dailyData?.[1].rawNightTemperature,
      }).replace(missingValue, 'unknown');

  const day3Label = error
    ? ''
    : t('tileDayAccessibilityLabel', {
        day: dailyData?.[2].day,
        weather: dailyData?.[2].weather,
        temperature: dailyData?.[2].rawTemperature,
        nightTemperature: dailyData?.[2].rawNightTemperature,
      }).replace(missingValue, 'unknown');

  return (
    <TWCTile
      accessibilityHint={t('tileAccessibilityHint')}
      accessibilityLabel={
        error
          ? t('errorAccessibilityLabel')
          : t('tileAccessibilityLabel', {
              day1: day1Label,
              day2: day2Label,
              day3: day3Label,
            })
      }
      height={198}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
      testID={myWeatherPageTestIDs.dailyForecastTile}
      width={516}
      onPress={handleOnPress}
    >
      <View style={styles.weatherTileDetailHeaderWrapper}>
        <TWCText testID={dailyWeatherConditionsTestIDs.headerText} variant={'regularText24Transparent'}>
          {t('headerText')}
        </TWCText>
      </View>
      <View style={styles.weatherTileDetailContentWrapper}>
        {isLoading ? (
          <ActivityIndicator color={Theme.loading} size={'large'} />
        ) : (
          <View style={styles.weatherTileDetailTimePeriodWrapper}>
            <WeatherTileDetail data={dailyData?.[0]} error={error} testID={dailyWeatherConditionsTestIDs.today} />
            <WeatherTileDetail data={dailyData?.[1]} error={error} testID={dailyWeatherConditionsTestIDs.tomorrow} />
            <WeatherTileDetail
              data={dailyData?.[2]}
              error={error}
              testID={dailyWeatherConditionsTestIDs.dayAfterTomorrow}
            />
          </View>
        )}
      </View>
    </TWCTile>
  );
};

DailyWeatherConditionsTileUI.propTypes = {
  data: PropTypes.object,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

DailyWeatherConditionsTileUI.defaultProps = {
  data: null,
  error: null,
  isLoading: false,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
};

export const DailyWeatherConditionsTile = {
  API: DailyWeatherConditionsTileAPI,
  UI: DailyWeatherConditionsTileUI,
};

const styles = StyleSheet.create({
  weatherTileDetailContentWrapper: {
    borderBottomLeftRadius: Theme.tileBorderRadius,
    borderBottomRightRadius: Theme.tileBorderRadius,
    elevation: 500,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  weatherTileDetailHeaderWrapper: {
    paddingLeft: Theme.px(30),
    paddingVertical: Theme.px(7),
  },
  weatherTileDetailTimePeriodWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: Theme.px(516),
  },
});
